import React, { useRef } from "react";
import PropTypes from "prop-types";
import "../styles/welcome.css";

/**
 * WelcomeSection
 * 
 * Adds optional YouTube embed in place of bottom image.
 * 
 * Props:
 * - headline: string (required)
 * - body: HTML string (required)
 * - topImage?: string
 * - bottomImage?: string
 * - videoId?: string (YouTube video ID, e.g. "UDyaAKTPti8")
 *
 * Behavior:
 * - If `videoId` is provided, it renders an embedded YouTube video.
 * - Otherwise, it falls back to rendering `bottomImage`.
 *
 * A11y:
 * - Headline = <h2>
 * - Video includes descriptive title and is responsive (16x9)
 */
const WelcomeSection = ({
  headline,
  body,
  topImage,
  bottomImage,
  videoId,
}) => {
  const iframeRef = useRef(null);

  return (
    <section className="welcome-section position-relative d-flex flex-column align-items-center justify-content-center text-center">
      {/* Decorative top image */}
      {topImage && (
        <img
          src={topImage}
          alt=""
          aria-hidden="true"
          className="welcome-top-img"
        />
      )}

      {/* Headline */}
      <h2 className="welcome-headline">{headline}</h2>

      {/* Body */}
      <p
        className="welcome-body"
        dangerouslySetInnerHTML={{ __html: body }}
      />

      {/* Bottom media: YouTube or image */}
      {videoId ? (
        <div className="about-video">
          <div className="ratio-16x9">
            <iframe
              ref={iframeRef}
              src="https://www.youtube.com/embed/UDyaAKTPti8?controls=1&rel=0&playsinline=1"
              title="Inside Our School"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          </div>
        </div>
      ) : (
        bottomImage && (
          <img
            src={bottomImage}
            alt=""
            className="welcome-bottom-img img-fluid"
          />
        )
      )}
    </section>
  );
};

WelcomeSection.propTypes = {
  headline: PropTypes.string.isRequired,
  body: PropTypes.string.isRequired,
  topImage: PropTypes.string,
  bottomImage: PropTypes.string,
  videoId: PropTypes.string,
};

export default WelcomeSection;

